import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CardOption } from '../interface/fee.interface';
import { FeeService } from '../service/fee.service';

const INITIAL_LOADING_STATE = true;
const FIRST_CARD_INDEX = 0;
const EMPTY_LIST_LENGTH = 0;

@Component({
  selector: 'app-fee-welcome-setup',
  templateUrl: './fee-welcome-setup.component.html',
  styleUrls: ['./fee-welcome-setup.component.scss']
})
export class FeeWelcomeSetupComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  @Input() industryId: number | null = null;
  @Output() setup = new EventEmitter<CardOption>();

  userCards: CardOption[] = [];
  selectedCard: CardOption | null = null;
  isCardMenuOpen = false;
  isLoadingCards = INITIAL_LOADING_STATE;

  constructor(
    private readonly feeService: FeeService,
    private readonly elementRef: ElementRef,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.isLoadingCards = INITIAL_LOADING_STATE;
    this.feeService.getAvailableCards(this.industryId ?? undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: cards => {
          this.userCards = cards;
          if (this.userCards.length > EMPTY_LIST_LENGTH) {
            this.selectedCard = this.userCards[FIRST_CARD_INDEX];
          }
          this.isLoadingCards = false;
        },
        error: () => {
          this.isLoadingCards = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedCardLabel(): string {
    if (!this.selectedCard) return '—';
    return this.formatCardLabel(this.selectedCard);
  }

  get selectedCardInitial(): string {
    const value = this.selectedCard?.name
      || this.selectedCard?.surname
      || this.selectedCard?.cardId
      || 'U';
    return value.trim().charAt(0).toUpperCase();
  }

  onSetup(): void {
    if (this.selectedCard) {
      this.setup.emit(this.selectedCard);
    }
  }

  goToIndividualCards(): void {
    this.router.navigate(['/cards']);
  }

  toggleCardMenu(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.userCards.length) return;
    this.isCardMenuOpen = !this.isCardMenuOpen;
  }

  selectCard(card: CardOption, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedCard = card;
    this.isCardMenuOpen = false;
  }

  formatCardLabel(card: CardOption): string {
    const value = card?.cardId?.trim() || '—';
    if (value === '—') return value;
    return value.startsWith('@') ? value : `@${value}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) return;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.isCardMenuOpen = false;
    }
  }
}
